import { ApiRequestConfig, apiService } from './api.service';

import { OrderStatus } from '@/components/chat/utils/order-status';
import { CreateOrderDto } from '@/interfaces/api/orders/CreateOrderDto';
import { ICollectionOrder } from '@/interfaces/api/orders/ICollectionOrder';
import { IOrder } from '@/interfaces/api/orders/IOrder';
import { OrderFilterQueryParamsDto } from '@/interfaces/api/orders/OrderFilterQueryParamsDto';
import { OrderSortQueryParamsDto } from '@/interfaces/api/orders/OrderSortQueryParamsDto';
import { IApiService } from '@/interfaces/services/IApiService';

export interface IOrdersApiService {
	createOrder(
		payload: CreateOrderDto,
		config?: ApiRequestConfig,
	): Promise<IOrder>;
	getOrders(
		page?: { number: number; size?: number },
		myOrdersCheck?: boolean,
		filter?: OrderFilterQueryParamsDto,
		sort?: OrderSortQueryParamsDto,
		config?: ApiRequestConfig,
	): Promise<ICollectionOrder>;
	getOrderById(id: string, config?: ApiRequestConfig): Promise<IOrder>;
	updateOrderStatus(
		id: string,
		status: OrderStatus,
		config?: ApiRequestConfig,
	): Promise<IOrder>;
	addTrackingNumber(
		id: string,
		trackingNumber: string,
		config?: ApiRequestConfig,
	): Promise<IOrder>;
	exportOrdersAsCSV(
		myOrdersCheck?: boolean,
		config?: ApiRequestConfig,
	): Promise<Blob>;
}

export class OrdersApiService implements IOrdersApiService {
	private api: IApiService<ApiRequestConfig>;

	constructor(api: IApiService<ApiRequestConfig>) {
		this.api = api;
	}

	async createOrder(
		payload: CreateOrderDto,
		config?: ApiRequestConfig,
	): Promise<IOrder> {
		return await this.api.post<IOrder>('/orders', payload, config);
	}

	async getOrders(
		page: { number: number; size?: number } = { number: 1, size: 10 },
		myOrdersCheck?: boolean,
		filter?: OrderFilterQueryParamsDto,
		sort?: OrderSortQueryParamsDto,
		config?: ApiRequestConfig,
	): Promise<ICollectionOrder> {
		const params: Record<string, any> = {};
		if (page) params.page = page;
		if (myOrdersCheck) params.myOrdersCheck = myOrdersCheck;
		if (filter) params.filter = filter;
		if (sort) params.sort = sort;
		return await this.api.get<ICollectionOrder>('/orders', {
			...config,
			params,
		});
	}

	async getOrderById(id: string, config?: ApiRequestConfig): Promise<IOrder> {
		return await this.api.get<IOrder>(`/orders/${id}`, config);
	}

	async updateOrderStatus(
		id: string,
		status: OrderStatus,
		config?: ApiRequestConfig,
	): Promise<IOrder> {
		return await this.api.patch<IOrder>(
			`/orders/${id}/status`,
			{ status },
			config,
		);
	}

	async addTrackingNumber(
		id: string,
		trackingNumber: string,
		config?: ApiRequestConfig,
	): Promise<IOrder> {
		return await this.api.patch<IOrder>(
			`/orders/${id}/tracking`,
			{ trackingNumber },
			config,
		);
	}

	async exportOrdersAsCSV(
		myOrdersCheck?: boolean,
		config?: ApiRequestConfig,
	): Promise<Blob> {
		const params: Record<string, any> = {};
		if (myOrdersCheck !== undefined) params.myOrdersCheck = myOrdersCheck;

		return await this.api.get<Blob>('/orders/export/csv', {
			...config,
			params,
			responseType: 'blob',
		});
	}
}

export const ordersApiService = new OrdersApiService(apiService);
